package com.oasisfeng.invaluable.p2p.core

import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.event.BatchWireCodec
import com.oasisfeng.invaluable.identity.Codec

/**
 * Ordinary-peer adapter for the opaque relay wire.
 *
 * The relay sees only complete UTF-8 capsules. Batch-v1 decoding, signature checks, semantic frontier maintenance and
 * duplicate suppression remain behind [P2pSession.Listener] in the peer. [durableCursor] is only a relay-local scan
 * watermark; losing it replays capsules and cannot mutate the peer's semantic frontier except through normal batch
 * validation. The adapter deliberately replays from zero for `WANT`, because the blind relay has no projection index.
 */
class OpaqueRelaySession internal constructor(
    id: Int,
    private val route: String,
    projector: LinkProjector,
    initialCursor: Long,
    private val confirmSemanticDurability: () -> Boolean,
    private val onCursorChanged: (Long) -> Unit,
    private val socket: dynamic,
    private val adapter: OpaqueRelaySocketAdapter,
    private val log: (String) -> Unit = {},
    private val onStateChanged: () -> Unit = {},
    private val schedulePoll: ((() -> Unit) -> Unit),
) : P2pSession {

    /**
     * Create one ordinary-peer session over an already-created WebSocket.
     *
     * [route] and [initialCursor] are local transport observations, not Link topology or semantic sync authority.
     * [projector] is used only by the peer-side batch stager; opaque relay responses are exact-byte verified before
     * their capsules enter the listener's existing semantic validation path. [confirmSemanticDurability] must bring
     * those accepted facts to durable storage before the relay-local cursor is allowed to advance.
     */
    constructor(
        id: Int,
        route: String,
        projector: LinkProjector,
        initialCursor: Long,
        confirmSemanticDurability: () -> Boolean,
        onCursorChanged: (Long) -> Unit,
        socket: dynamic,
        adapter: OpaqueRelaySocketAdapter,
        log: (String) -> Unit = {},
        onStateChanged: () -> Unit = {},
        pollIntervalMillis: Int = DEFAULT_POLL_INTERVAL_MILLIS,
    ) : this(id, route, projector, initialCursor, confirmSemanticDurability, onCursorChanged, socket, adapter, log,
        onStateChanged,
        { callback -> js("setTimeout")(callback, pollIntervalMillis.coerceAtLeast(1)) })

    /** Process-local connection phase observations for operator status only. */
    val timeline = SessionTimeline(SessionTimeline.epochOf("opaque", id))
    private val batchStager = BatchWireCodec(projector).Stager()
    private val batchLines = mutableListOf<String>()
    private val commands = ArrayDeque<RelayCommand>()
    private var activeCommand: RelayCommand.Wire? = null
    private var durableCursor = initialCursor.coerceIn(0, MAX_EXACT_SEQUENCE)
    private var scanAfter = durableCursor
    private var open = false
    private var closed = false
    private var caughtUp = false
    private var pollScheduled = false
    private var pollGeneration = 0
    private var protocolStarted = false

    override val outbound: Boolean = true
    override val isOpen: Boolean get() = open && ! closed
    /** Whether this local session wrapper has permanently closed. */
    val isClosed: Boolean get() = closed

    override var listener: P2pSession.Listener? = null
        set(value) {
            field = value
            startProtocolIfReady()
        }

    init {
        require(route.matches(Regex("[A-Za-z0-9._~-]{1,64}"))) { "Opaque relay route is invalid." }
        adapter.bindEvents(
            socket,
            onOpen = ::handleOpen,
            onMessage = { message -> timeline.markFirstSync(); receiveResponse(message) },
            onClose = { cause -> finishClose(cause) },
        )
        if (adapter.isOpen(socket)) handleOpen()
    }

    private fun handleOpen() {
        if (closed || open) return
        if (adapter.selectedProtocol(socket) != PROTOCOL) return fail("relay selected an unexpected protocol")
        open = true
        timeline.markHandshaked()
        onStateChanged()

        startProtocolIfReady()
    }

    private fun startProtocolIfReady() {
        if (! open || closed || protocolStarted || listener == null) return
        protocolStarted = true
        // An empty peer frontier makes ReplicatedEventSync emit a full local snapshot. Reconnect therefore repairs
        // any PUT lost with the previous socket; exact capsule bytes make those re-PUTs idempotent at the relay.
        listener?.onLine(SyncControlWire.FRONTIER_TAG)
        enqueue(RelayCommand.Cursor(route, scanAfter))
    }

    override fun sendRaw(line: String) {
        if (closed) return
        when (val staged = batchStager.accept(line)) {
            is BatchWireCodec.StageResult.Pending -> batchLines += line
            is BatchWireCodec.StageResult.Complete -> {
                batchLines += line
                publishCapsule(batchLines.joinToString("\n"))
                batchLines.clear()
            }
            is BatchWireCodec.StageResult.Violation -> fail("invalid outbound batch: ${staged.reason}")
            is BatchWireCodec.StageResult.NotBatch -> when {
                SyncControlWire.isFrontier(line) || line == SyncControlWire.SYNCED_TAG -> Unit
                SyncControlWire.isWant(line) -> rescanFromZero()
                line.startsWith(DirectedEnvelope.TAG + "|") -> publishCapsule(line)
                else -> fail("unsupported outbound peer frame")
            }
        }
    }

    override fun close() {
        if (closed) return
        cancelPoll()
        closed = true
        open = false
        runCatching { adapter.clearEvents(socket) }
        runCatching { socket.close(1000, "peer closed") }
        listener?.onClosed()
        onStateChanged()
    }

    private fun publishCapsule(capsule: String) {
        val bytes = capsule.encodeToByteArray()
        if (bytes.isEmpty() || bytes.size > BatchWireCodec.MAX_WIRE_BYTES) {
            return fail("outbound capsule exceeds relay limit")
        }
        val blobId = Codec.base64Url(Codec.sha256(bytes))
        enqueue(RelayCommand.Put("PUT $route ${Codec.base64Url(bytes)}", blobId, bytes.size))
    }

    private fun rescanFromZero() {
        cancelPoll()
        caughtUp = false
        scanAfter = 0
        enqueueFirst(RelayCommand.Cursor(route, 0))
    }

    private fun enqueue(command: RelayCommand) {
        commands.addLast(command)
        pump()
    }

    private fun enqueueFirst(command: RelayCommand) {
        commands.addFirst(command)
        pump()
    }

    private fun pump() {
        if (! isOpen || activeCommand != null) return
        while (commands.isNotEmpty()) when (val command = commands.removeFirst()) {
            is RelayCommand.CommitCursor -> {
                scanAfter = command.scannedThrough
                if (scanAfter != durableCursor) {
                    if (! confirmDurability()) return
                    durableCursor = scanAfter
                    if (! persistCursor(durableCursor)) return
                }
                if (command.offerCount == 0 && scanAfter == command.requestedAfter) {
                    if (! caughtUp) listener?.onLine(SyncControlWire.SYNCED_TAG)
                    caughtUp = true
                    scheduleNextPoll()
                } else {
                    enqueueFirst(RelayCommand.Cursor(route, scanAfter))
                }
            }
            is RelayCommand.Wire -> {
                activeCommand = command
                adapter.send(socket, command.line)
                return
            }
        }
    }

    private fun receiveResponse(message: String) {
        val command = activeCommand ?: return fail("unsolicited relay response")
        val envelope = decodeResponseEnvelope(message) ?: return fail("malformed relay response envelope")
        adapter.send(socket, "ACK ${envelope.token}")
        activeCommand = null

        when (command) {
            is RelayCommand.Put -> {
                val offer = parseOffer(envelope.payload, route) ?: return fail("relay rejected capsule PUT")
                if (offer.blobId != command.blobId || offer.byteSize != command.byteSize) {
                    return fail("relay PUT response changed capsule identity")
                }
            }
            is RelayCommand.Cursor -> receiveCursor(command, envelope.payload)
            is RelayCommand.Pull -> receivePull(envelope.payload, command.blobId, command.byteSize)
        }
        pump()
    }

    private fun receiveCursor(command: RelayCommand.Cursor, payload: String) {
        val page = parseCursorPage(payload, route) ?: return fail("malformed relay cursor page")
        if (page.scannedThrough < command.after && page.offers.isNotEmpty()) {
            return fail("relay returned offers while clamping a future cursor")
        }
        if (page.scannedThrough >= command.after &&
            page.offers.any { it.sequence <= command.after || it.sequence > page.scannedThrough }) {
            return fail("relay returned an offer outside its cursor window")
        }
        val followUps = page.offers.map { RelayCommand.Pull(it.blobId, it.byteSize) } + RelayCommand.CommitCursor(
            requestedAfter = command.after,
            scannedThrough = page.scannedThrough,
            offerCount = page.offers.size,
        )
        for (followUp in followUps.asReversed()) commands.addFirst(followUp)
    }

    private fun receivePull(payload: String, expectedBlobId: String, expectedByteSize: Int) {
        if (payload in PULL_REJECTIONS) return
        if (payload.startsWith("REJECT ")) return fail("relay returned an unknown PULL rejection")
        val prefix = "DATA $expectedBlobId "
        if (! payload.startsWith(prefix)) return fail("relay returned a mismatched DATA identity")
        val encoded = payload.substring(prefix.length)
        val bytes = runCatching { Codec.base64UrlDecode(encoded) }.getOrNull()
            ?: return fail("relay returned malformed DATA bytes")
        if (bytes.size != expectedByteSize || Codec.base64Url(bytes) != encoded ||
            Codec.base64Url(Codec.sha256(bytes)) != expectedBlobId) {
            return fail("relay DATA failed peer-side exact-byte verification")
        }
        val capsule = runCatching { bytes.decodeToString(throwOnInvalidSequence = true) }.getOrNull()
            ?: return fail("relay DATA capsule is not UTF-8")
        val delivered = runCatching {
            when {
                capsule.startsWith("BATCH|1|") -> deliverBatchCapsule(capsule)
                capsule.startsWith(DirectedEnvelope.TAG + "|") && '\n' !in capsule -> listener?.onLine(capsule)
                else -> log("opaque relay: ignored unsupported capsule ${expectedBlobId.take(12)}")
            }
        }
        if (delivered.isFailure) {
            fail("peer semantic intake failed")
        }
    }

    private fun deliverBatchCapsule(capsule: String) {
        val lines = capsule.split('\n')
        if (lines.size < 3 || ! lines.last().startsWith("END|") ||
            lines.subList(1, lines.lastIndex).any(BatchWireCodec::isBatchBoundary)) {
            return fail("relay DATA contains a malformed batch capsule")
        }
        for (line in lines) {
            if (closed) return
            listener?.onLine(line)
        }
    }

    private fun fail(reason: String) {
        if (closed) return
        log("opaque relay: $reason")
        cancelPoll()
        closed = true
        open = false
        runCatching { adapter.clearEvents(socket) }
        runCatching { socket.close(1002, reason) }
        listener?.onClosed()
        onStateChanged()
    }

    private fun finishClose(cause: String) {
        if (closed) return
        cancelPoll()
        closed = true
        open = false
        runCatching { adapter.clearEvents(socket) }
        runCatching { socket.close() }
        listener?.onClosed()
        log("opaque relay: socket closed$cause")
        onStateChanged()
    }

    private fun scheduleNextPoll() {
        if (pollScheduled || ! isOpen || ! caughtUp) return
        pollScheduled = true
        val generation = ++pollGeneration
        schedulePoll {
            if (generation != pollGeneration) return@schedulePoll
            pollScheduled = false
            if (isOpen && caughtUp) enqueue(RelayCommand.Cursor(route, durableCursor))
        }
    }

    private fun cancelPoll() {
        pollGeneration++
        pollScheduled = false
    }

    private fun persistCursor(cursor: Long): Boolean {
        if (runCatching { onCursorChanged(cursor) }.isSuccess) return true
        fail("relay cursor persistence failed")
        return false
    }

    private fun confirmDurability(): Boolean {
        if (runCatching { confirmSemanticDurability() }.getOrDefault(false)) return true
        fail("peer semantic durability failed")
        return false
    }

    private sealed interface RelayCommand {
        sealed interface Wire : RelayCommand { val line: String }
        data class Put(override val line: String, val blobId: String, val byteSize: Int) : Wire
        data class Cursor(val route: String, val after: Long) : Wire { override val line: String = "CURSOR $route $after" }
        data class Pull(val blobId: String, val byteSize: Int) : Wire { override val line: String = "PULL $blobId" }
        data class CommitCursor(val requestedAfter: Long, val scannedThrough: Long, val offerCount: Int) : RelayCommand
    }

    companion object {
        /** The only WebSocket subprotocol spoken by the opaque relay adapter. */
        const val PROTOCOL = "invaluable.opaque.v2"
        /** Default delay between caught-up cursor scans for later offers. */
        const val DEFAULT_POLL_INTERVAL_MILLIS = 2_000

        /** Whether [url] explicitly selects opaque v2 and contains a valid ephemeral route. */
        fun isUrl(url: String): Boolean = runCatching {
            val parsed: dynamic = js("new URL(url)")
            (parsed.searchParams.get("transport") as? String) == "opaque-v2" && routeFromUrl(url) != null
        }.getOrDefault(false)

        /** Parse the ephemeral relay route selected by [url], or null when it is absent or malformed. */
        fun routeFromUrl(url: String): String? = runCatching {
            val parsed: dynamic = js("new URL(url)")
            (parsed.searchParams.get("route") as? String)
                ?.takeIf { it.matches(Regex("[A-Za-z0-9._~-]{1,64}")) }
        }.getOrNull()
    }
}

/** Host WebSocket operations required by [OpaqueRelaySession], without importing browser, Node or Workers APIs. */
interface OpaqueRelaySocketAdapter {
    fun isOpen(socket: dynamic): Boolean
    fun selectedProtocol(socket: dynamic): String?
    fun send(socket: dynamic, message: String)
    fun bindEvents(
        socket: dynamic,
        onOpen: () -> Unit,
        onMessage: (String) -> Unit,
        onClose: (String) -> Unit,
    )
    fun clearEvents(socket: dynamic)

    companion object {
        /** DOM-style adapter shared by browsers and Cloudflare's accepted outbound WebSocket. */
        val Dom = object : OpaqueRelaySocketAdapter {
            override fun isOpen(socket: dynamic): Boolean = socket.readyState == 1
            override fun selectedProtocol(socket: dynamic): String? = socket.protocol as? String
            override fun send(socket: dynamic, message: String) { socket.send(message) }
            override fun bindEvents(
                socket: dynamic,
                onOpen: () -> Unit,
                onMessage: (String) -> Unit,
                onClose: (String) -> Unit,
            ) {
                socket.onopen = { onOpen() }
                socket.onmessage = { event: dynamic ->
                    val data = event.data as? String
                    if (data != null) onMessage(data) else onClose(" error=non-text response")
                }
                socket.onclose = { event: dynamic ->
                    val code = event?.code as? Int
                    val reason = event?.reason as? String
                    onClose(buildString {
                        if (code != null) append(" code=").append(code)
                        if (! reason.isNullOrBlank()) append(" reason=").append(reason)
                    })
                }
                socket.onerror = { onClose(" error=socket error") }
            }
            override fun clearEvents(socket: dynamic) {
                socket.onopen = null; socket.onmessage = null; socket.onclose = null; socket.onerror = null
            }
        }
    }
}

private data class RelayResponse(val payload: String, val token: String)
private data class RelayOffer(val sequence: Long, val blobId: String, val byteSize: Int)
private data class RelayCursorPage(val offers: List<RelayOffer>, val scannedThrough: Long)
private const val MAX_EXACT_SEQUENCE = 9_007_199_254_740_991L
private const val MAX_CURSOR_PAGE_LINES = 257
private val PULL_REJECTIONS = setOf("REJECT missing", "REJECT revoked", "REJECT corrupt", "REJECT too-large")

private fun decodeResponseEnvelope(message: String): RelayResponse? {
    val prefix = "RESPONSE\n"
    if (! message.startsWith(prefix)) return null
    val newline = message.lastIndexOf('\n')
    if (newline < prefix.length) return null
    val token = message.substring(newline + 1)
    if (! token.matches(Regex("[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}"))) return null
    return RelayResponse(message.substring(prefix.length, newline), token)
}

private fun parseCursorPage(payload: String, route: String): RelayCursorPage? {
    val lines = payload.split('\n')
    if (lines.size !in 1..MAX_CURSOR_PAGE_LINES) return null
    val cursor = lines.lastOrNull()?.split(' ') ?: return null
    if (cursor.size != 3 || cursor[0] != "CURSOR" || cursor[1] != route) return null
    val scannedThrough = exactSequence(cursor[2]) ?: return null
    val offers = mutableListOf<RelayOffer>()
    for (line in lines.dropLast(1)) {
        offers += parseOffer(line, route) ?: return null
    }
    return RelayCursorPage(offers, scannedThrough)
}

private fun parseOffer(line: String, route: String): RelayOffer? {
    val fields = line.split(' ')
    if (fields.size != 7 || fields[0] != "OFFER" || fields[1] != route) return null
    val sequence = exactSequence(fields[2])?.takeIf { it > 0 } ?: return null
    if (exactSequence(fields[3])?.takeIf { it > 0 } == null) return null
    if (! fields[4].matches(Regex("[A-Za-z0-9_-]{43}"))) return null
    val byteSize = fields[5].toIntOrNull()?.takeIf { it in 1..BatchWireCodec.MAX_WIRE_BYTES } ?: return null
    if (exactSequence(fields[6])?.takeIf { it > 0 } == null) return null
    return RelayOffer(sequence, fields[4], byteSize)
}

private fun exactSequence(raw: String): Long? {
    if (raw.isEmpty() || raw.length > 16 || raw.any { it !in '0'..'9' }) return null
    if (raw.length > 1 && raw[0] == '0') return null
    return raw.toLongOrNull()?.takeIf { it in 0..MAX_EXACT_SEQUENCE }
}
